import { ChangeDetectorRef, Component, Inject, Input } from '@angular/core';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { SelectionModel } from '@angular/cdk/collections';
import { of, Observable } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FilterTreeNode } from './filters.class';
import { take } from 'rxjs/operators';
import { FilterTreeJSON } from './filters.interface';

/**
 * Dynamic filter tree for element filtering.
 *
 * Creates a tree based on a JSON input.
 *
 * Based on https://stackblitz.com/edit/material-tree-checklist-re96eq?file=app%2Fapp.component.ts.
 */
@Component({
  selector: 'sg2-filters-tree',
  templateUrl: './filters.component.html',
  styleUrls: ['./filters.component.less']
})
export class ModalFiltersComponent {

  /** The selection for checklist */
  private checklistSelection = new SelectionModel<FilterTreeNode>(true);

  /** Tree data source object. */
  public dataSource: MatTreeFlatDataSource<FilterTreeNode, FilterTreeNode>;

  /** Create level map of the tree. */
  private levels = new Map<FilterTreeNode, number>();

  /** Current tree state. */
  private tree$: Observable<FilterTreeNode[]>;

  /** Controller for the Tree component. */
  public treeControl: FlatTreeControl<FilterTreeNode>;

  /** Flattens tree for use in Tree component. */
  private treeFlattener: MatTreeFlattener<FilterTreeNode, FilterTreeNode>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly treeInput: FilterTreeJSON,
    private changeDetectorRef: ChangeDetectorRef,
    public dialogRef: MatDialogRef<ModalFiltersComponent>
  ) {
    this.treeFlattener = new MatTreeFlattener(this._transformer, this._getLevel, this._isExpandable, this._getChildren);
    this.treeControl = new FlatTreeControl<FilterTreeNode>(this._getLevel, this._isExpandable);
    this.dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

    this._initTree();
  }

  /**
   * Select/Deselect checkboxes.
   *
   * - `all`: Check all chekcboxes, children included.
   * - `none`: Uncheck all checkboxes, children included.
   * - `toggle`: Toggle all checkboxes, children included.
   *
   * @param selection Toggle method. All, none or toggle.
   */
  public check(selection?: 'all' | 'none' | 'toggle'): void {
    for (const node of this.treeControl.dataNodes) {
      switch (selection) {
        case 'all': // Select all
          if (!this.checklistSelection.isSelected(node)) {
            this.checklistSelection.toggle(node);
          }
          break;
        case 'none': // Deselect all
          if (this.checklistSelection.isSelected(node)) {
            this.checklistSelection.toggle(node);
          }
          break;
        case 'toggle': // Toggle
          this.checklistSelection.toggle(node);
          break;
      }
    }
  }

  /**
   * Check if all the child elements are selected.
   *
   * @param node The node to check.
   * @returns Whether the node has all its children selected.
   */
  public descendantsAllSelected(node: FilterTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    if (!descendants.length) {
      return this.checklistSelection.isSelected(node);
    }
    const selected = this.checklistSelection.isSelected(node);
    const allSelected = descendants.every(child => this.checklistSelection.isSelected(child));
    if (!selected && allSelected) {
      this.checklistSelection.select(node);
      this.changeDetectorRef.markForCheck();
    }
    return allSelected;
  }

  /**
   * Whether *part* of the child nodes are selected.
   *
   * @param node The node to check.
   * @returns Whether part of the child are selected.
   */
  public descendantsPartiallySelected(node: FilterTreeNode): boolean {
    const descendants = this.treeControl.getDescendants(node);
    if (!descendants.length) {
      return false;
    }
    const result = descendants.some(child => this.checklistSelection.isSelected(child));
    return result && !this.descendantsAllSelected(node);
  }

  /**
   * Check if the current node has any children.
   *
   * @param index Index of node in current tree (flattened).
   * @param node The node to check.
   * @returns Whether the node has children.
   */
  public hasChildren = (index: number, node: FilterTreeNode): boolean => {
    return this._isExpandable(node);
  }

  /**
   * Select or unselect nodes.
   *
   * When parent node, select or deselect all child notes too.
   *
   * @param node The node to check.
   */
  public nodeSelectionToggle(node: FilterTreeNode): void {
    this.checklistSelection.toggle(node);
    const descendants = this.treeControl.getDescendants(node);
    this.checklistSelection.isSelected(node)
      ? this.checklistSelection.select(...descendants, node)
      : this.checklistSelection.deselect(...descendants, node);
    this.changeDetectorRef.markForCheck();
  }

  /**
   * Submit data back to parent component.
   *
   * @todo Actually return data.
   */
  public submit() {
    this.tree$.pipe(take(1)).subscribe(treeNodes => {
      this.dialogRef.close(
        this._inputFromTreeNodes(treeNodes)
      );
    });
  }

  /**
   * Get node's children.
   *
   * @param node The node to get the children for.
   * @returns The list of child nodes.
   */
  private _getChildren = (node: FilterTreeNode) => {
    return node.children;
  }

  /**
   * Get nested level of current node.
   *
   * @param node Current tree node.
   * @returns The nested level of the current node.
   */
  private _getLevel = (node: FilterTreeNode): number => {
    return this.levels.get(node) || 0;
  }

  /**
   * Initialize tree based on input data.
   */
  private _initTree(): void {
    this.tree$ = this._inputToTreeNodes(this.treeInput);
    this.tree$.subscribe(data => {
      this.dataSource.data = data;
      for (const children of data) {
        const descendants = this.treeControl.getDescendants(children);
        for (const node of descendants) {
          if (node.active) {
            this.checklistSelection.select(node);
          }
        }
      }
    });
  }

  /**
   * Whether the current node can/may be expanded.
   *
   * @param node The current node.
   * @returns Whether the current node can/may be expanded.
   */
  private _isExpandable = (node: FilterTreeNode): boolean => {
    return node.children.value.length > 0;
  }

  /**
   * Convert TreeNodes back to input JSON.
   *
   * @param treeNodes TreeNodes object.
   * @returns Standard JSON object.
   */
  private _inputFromTreeNodes(treeNodes: FilterTreeNode[]): FilterTreeJSON {
    const Json: FilterTreeJSON = [];
    for (const parent of treeNodes) {
      const children = [];
      for (const child of parent.children.getValue()) {

        // Child node.
        children.push({
          value: child.value,
          active: this.checklistSelection.isSelected(child),
          key: child.key,
          label: child.label
        });
      }

      // Parent node.
      Json.push({
        label: parent.label,
        children: children
      });
    }
    return Json;
  }

  /**
   * Convert input JSON to TreeNodes.
   *
   * @param treeInput The input data to parse into valid Tree data.
   * @returns Valid Observable for tree builder.
   */
  private _inputToTreeNodes(treeInput: FilterTreeJSON): Observable<FilterTreeNode[]> {
    const tree: FilterTreeNode[] = [];
    for (const parent of treeInput) {
      const children = [];
      for (const child of parent.children) {

        // Child node.
        children.push(new FilterTreeNode(
          child.label,
          child.value,
          child.key,
          child.active
        ));
      }

      // Parent node.
      tree.push(new FilterTreeNode(
        parent.label,
        null,
        null,
        null,
        children
      ));
    }
    return of(tree);
  }

  /**
   * Transforms flat tree data to nested list.
   *
   * @param node Current node.
   * @param level The asigned/requested level of the node.
   * @returns The current node.
   */
  private _transformer = (node: FilterTreeNode, level: number): FilterTreeNode => {
    this.levels.set(node, level);
    return node;
  }
}
